<template>
  <div>
    <!-- Modal Structure -->
    <div
      v-if="showModal"
      class="modal fade show d-block"
      tabindex="-1"
      role="dialog"
      aria-labelledby="audienceConditionsLabel"
      style="background: rgba(0, 0, 0, 0.8); z-index: 1060;"
      @click.self="closeModal"
    >
      <div class="modal-dialog modal-lg" role="document">
        <div class="modal-content bg-dark text-light">
          <div class="modal-header border-bottom border-secondary">
            <h5 class="modal-title" id="audienceConditionsLabel">
              <i class="fas fa-filter"></i> Audience Conditions: {{ audienceName }}
            </h5>
            <button type="button" class="close text-light" @click="closeModal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <div v-if="!audience || !audience.conditions || audience.conditions.length === 0" class="text-center text-muted py-4">
              <i class="fas fa-info-circle fa-2x mb-3"></i>
              <p>No conditions defined for this audience.</p>
            </div>
            <div v-else>
              <div class="audience-info mb-3">
                <div class="info-row">
                  <strong>Name:</strong> {{ audience.name }}
                </div>
                <div class="info-row" v-if="audience.description">
                  <strong>Description:</strong> {{ audience.description }}
                </div>
                <div class="info-row" v-if="audience.percentage !== undefined">
                  <strong>Percentage:</strong> {{ audience.percentage }}%
                </div>
              </div>
              
              <h6 class="mb-3 text-info">
                <i class="fas fa-list-ul"></i> Conditions:
              </h6>
              
              <div v-for="(condition, index) in audience.conditions" :key="`condition-${index}`">
                <div class="condition-card">
                  <div class="condition-header">
                    Condition {{ index + 1 }}
                  </div>
                  <div class="condition-body">
                    <div class="condition-row">
                      <span class="condition-label">Key:</span>
                      <span class="condition-value">
                        {{ condition.key === 'relative_created_at' ? 'created_at (relative - in days)' : condition.key }}
                      </span>
                    </div>
                    <div class="condition-row">
                      <span class="condition-label">Operator:</span>
                      <span class="condition-value operator-badge">{{ condition.operator }}</span>
                    </div>
                    <div class="condition-row" v-if="condition.custom_key">
                      <span class="condition-label">Custom Key:</span>
                      <span class="condition-value">{{ condition.custom_key }}</span>
                    </div>
                    <div class="condition-row">
                      <span class="condition-label">Value:</span>
                      <span class="condition-value">{{ formatValue(condition) }}</span>
                    </div>
                  </div>
                </div>
                <div v-if="index !== audience.conditions.length - 1" class="condition-separator">
                  <span v-if="condition.hasOr" class="or-separator">OR</span>
                  <span v-else class="and-separator">AND</span>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer border-top border-secondary">
            <button type="button" class="btn btn-secondary" @click="closeModal">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AudienceConditions',
  props: {
    audience: {
      type: Object,
      default: null
    },
    audienceName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      showModal: false
    };
  },
  methods: {
    openModal() {
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.$emit('modal-closed');
    },
    formatValue(condition) {
      const value = condition.value;
      
      // Handle date values
      if (condition.key === 'created_at' || condition.key === 'update_time') {
        try {
          return new Date(value).toLocaleString();
        } catch (e) {
          return value;
        }
      }
      
      // Handle arrays (multi-select values)
      if (Array.isArray(value)) {
        return value.join(', ');
      }
      
      // Handle objects
      if (typeof value === 'object' && value !== null) {
        return JSON.stringify(value);
      }
      
      // Handle regular values
      return value !== undefined && value !== null ? value.toString() : 'N/A';
    }
  }
};
</script>

<style scoped>
.modal-dialog {
  max-width: 700px;
}

.modal-header {
  background-color: rgba(45, 55, 72, 0.95);
}

.audience-info {
  background-color: rgba(113, 128, 150, 0.1);
  border-radius: 6px;
  padding: 1rem;
  border: 1px solid rgba(113, 128, 150, 0.2);
}

.info-row {
  margin-bottom: 0.5rem;
  color: #cbd5e0;
}

.info-row:last-child {
  margin-bottom: 0;
}

.info-row strong {
  color: #e2e8f0;
  margin-right: 0.5rem;
}

.condition-card {
  background-color: rgba(49, 130, 206, 0.1);
  border-radius: 8px;
  border: 1px solid rgba(49, 130, 206, 0.2);
  overflow: hidden;
  margin-bottom: 0.5rem;
}

.condition-header {
  background-color: rgba(49, 130, 206, 0.15);
  padding: 0.5rem 1rem;
  font-weight: 600;
  color: #63b3ed;
  border-bottom: 1px solid rgba(49, 130, 206, 0.2);
  font-size: 14px;
}

.condition-body {
  padding: 1rem;
}

.condition-row {
  display: flex;
  margin-bottom: 0.75rem;
  align-items: flex-start;
}

.condition-row:last-child {
  margin-bottom: 0;
}

.condition-label {
  min-width: 120px;
  font-weight: 600;
  color: #a0aec0;
  font-size: 13px;
}

.condition-value {
  flex: 1;
  color: #e2e8f0;
  word-break: break-word;
  font-size: 14px;
}

.operator-badge {
  display: inline-block;
  background-color: rgba(66, 153, 225, 0.2);
  color: #4299e1;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid rgba(66, 153, 225, 0.3);
}

.condition-separator {
  text-align: center;
  margin: 1rem 0;
  position: relative;
}

.condition-separator::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(to right, transparent, rgba(113, 128, 150, 0.3), transparent);
}

.or-separator,
.and-separator {
  position: relative;
  display: inline-block;
  padding: 0.25rem 1rem;
  font-weight: bold;
  font-size: 13px;
  border-radius: 4px;
  z-index: 1;
}

.or-separator {
  background-color: rgba(255, 193, 7, 0.2);
  color: #ffc107;
  border: 1px solid rgba(255, 193, 7, 0.3);
}

.and-separator {
  background-color: rgba(56, 161, 105, 0.2);
  color: #48bb78;
  border: 1px solid rgba(56, 161, 105, 0.3);
}

.modal-title {
  color: #ffffff !important;
  font-weight: 600;
}

.modal-title i {
  color: #63b3ed !important;
  margin-right: 0.5rem;
}

.close {
  color: #ffffff !important;
  opacity: 1;
  text-shadow: none;
  font-size: 2rem;
  font-weight: 300;
}

.close:hover {
  opacity: 1;
  color: #ff6b6b !important;
}

.modal-body {
  max-height: calc(100vh - 250px);
  overflow-y: auto;
}

/* Scrollbar styling */
.modal-body::-webkit-scrollbar {
  width: 8px;
}

.modal-body::-webkit-scrollbar-track {
  background: rgba(113, 128, 150, 0.1);
  border-radius: 4px;
}

.modal-body::-webkit-scrollbar-thumb {
  background: rgba(113, 128, 150, 0.3);
  border-radius: 4px;
}

.modal-body::-webkit-scrollbar-thumb:hover {
  background: rgba(113, 128, 150, 0.5);
}
</style>

